import {
    type ColumnDef,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    type ColumnFiltersState,
    type SortingState,
    type Table,
} from '@tanstack/react-table';
import * as React from 'react';

export interface UseDataTableOptions<TData extends object> {
    data: TData[];
    columns: ColumnDef<TData, any>[];

    // features
    enableGlobalSearch?: boolean;
    enablePagination?: boolean;

    // pagination config
    pageSizeOptions?: number[];
    initialPageSize?: number;

    // future ready: server-side pagination
    manualPagination?: boolean;
    pageCount?: number;
}

export interface UseDataTableResult<TData extends object> {
    table: Table<TData>;

    // search
    globalFilter: string;
    setGlobalFilter: (value: string) => void;

    // sorting
    sorting: SortingState;
    setSorting: React.Dispatch<React.SetStateAction<SortingState>>;

    // column filters (if needed later)
    columnFilters: ColumnFiltersState;
    setColumnFilters: React.Dispatch<React.SetStateAction<ColumnFiltersState>>;

    // pagination meta
    pageSizeOptions: number[];
}

export function useDataTable<TData extends object>(
    options: UseDataTableOptions<TData>,
): UseDataTableResult<TData> {
    const {
        data,
        columns,
        enableGlobalSearch = true,
        enablePagination = true,
        pageSizeOptions = [10, 20, 50],
        initialPageSize,
        manualPagination,
        pageCount,
    } = options;

    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] =
        React.useState<ColumnFiltersState>([]);
    const [globalFilter, setGlobalFilter] = React.useState<string>('');

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            columnFilters,
            globalFilter: enableGlobalSearch ? globalFilter : '',
        },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: enablePagination
            ? getPaginationRowModel()
            : undefined,

        manualPagination,
        pageCount,
        initialState: {
            pagination: {
                pageSize: initialPageSize ?? pageSizeOptions[0] ?? 10,
            },
        },
    });

    return {
        table,
        globalFilter,
        setGlobalFilter,
        sorting,
        setSorting,
        columnFilters,
        setColumnFilters,
        pageSizeOptions,
    };
}
